import React, { FC, useContext, useState } from 'react';
import { useSnackbar } from 'notistack';
import { MinimalPage, RedirectUser } from '../../components';
import { UserContext } from '../../context';
import makeStyles from '@mui/styles/makeStyles';
import { Box, FormControl, TextField, Alert, Theme } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { Formik, FormikHelpers, Field, FieldProps, FormikProps, Form } from 'formik';
import * as Yup from 'yup';
import { Button } from '../../components';
import { sendResetLink } from '../../fetch';
import { useHistory } from 'react-router-dom';

const ForgotPasswordSchema = Yup.object().shape({
  email: Yup.string().email('Email is not valid').required('Required')
});

interface IForgotPasswordValues {
  email: string;
}

export const ForgotPassword: FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user, isLoading } = useContext(UserContext);
  const classes = useStyles();
  const [loading, setLoading] = useState(false);

  const history = useHistory();

  const handleOnSubmit = () => {
    setTimeout(() => {
      history.push(`/login`);
    }, 5000);
  };

  if (isLoading) {
    return null;
  }

  if (user && !isLoading) {
    return <RedirectUser />;
  }

  return (
    <MinimalPage title="Forgot Password">
      <Formik
        initialValues={{
          email: ''
        }}
        onSubmit={async (values: IForgotPasswordValues, actions: FormikHelpers<IForgotPasswordValues>) => {
          setLoading(true);
          try {
            await sendResetLink(values);
            enqueueSnackbar(`If you are a user that exists in our system we will send you an email.`, {
              variant: 'success'
            });
            handleOnSubmit();
          } catch (error) {
            enqueueSnackbar(error?.Detail ?? 'Something went wrong, please try again.', {
              variant: 'error'
          });
          } finally {
            actions.setSubmitting(false);
            setLoading(false);
          }
        }}
        validationSchema={ForgotPasswordSchema}
      >
        {(formikProps: FormikProps<IForgotPasswordValues>) => {
          return (
            <>
              <Form data-testid="forgot-password-form" id="forgot-password-form" onSubmit={formikProps.handleSubmit}>
                <Box mt={1}>
                  <Alert severity="info">Enter your email address below and we will email you a code to reset your password.</Alert>
                </Box>
                <FormControl fullWidth={true} margin="normal">
                  <Field name="email">
                    {({ field, form }: FieldProps<IForgotPasswordValues>) => (
                      <TextField
                        {...field}
                        error={form.touched.email && form.errors.email ? true : false}
                        fullWidth={true}
                        helperText={form.touched.email && form.errors.email}
                        id="forgot-email"
                        label="Email"
                        onChange={e => formikProps.setFieldValue('email', e.target.value)}
                        type="email"
                      />
                    )}
                  </Field>
                </FormControl>
                <FormControl fullWidth={true} margin="normal">
                  <Button
                    color="primary"
                    disabled={!formikProps.isValid || loading || !formikProps.values.email}
                    fullWidth={true}
                    id="send-reset-link"
                    loading={loading ? 'Sending link...' : undefined}
                    size="large"
                    type="submit"
                    variant="contained"
                  >
                    Send Reset Link
                  </Button>
                </FormControl>
                <FormControl fullWidth={true} margin="normal">
                  <Button className={classes.back} color="primary" fullWidth={true} id="back-to-login" asLink={true} to={'/'} variant="text" startIcon={<ArrowBack />}>
                    Back to Login
                  </Button>
                </FormControl>
              </Form>
            </>
          );
        }}
      </Formik>
    </MinimalPage>
  );
};

const useStyles = makeStyles((theme: Theme) => {
  return {
    back: {
      textDecoration: 'none',
      textTransform: 'none',
      '&:hover': {
        textDecoration: 'underline'
      }
    },
    icon: {
      height: '15px',
      marginRight: theme.spacing(1),
      width: '15px !important'
    },
    paper: {
      maxWidth: 360
    },
    instructions: {
      marginTop: 16
    }
  };
});
